const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function getToken() {
  return localStorage.getItem('pp_token');
}

function translateError(msg, status) {
  if (status === 401) return 'Your Jira session has expired. Please sign in again.';
  if (status === 429) return 'Too many Jira updates today. Try again in a few minutes.';
  if (status === 500) return 'Something went wrong on the server. Please try again.';
  if (!msg) return 'Something went wrong. Please try again.';
  if (msg.includes('duplicate key') && msg.includes('teams_name_key'))
    return 'A team with that name already exists.';
  if (msg.includes('duplicate key') && msg.includes('sessions_'))
    return 'A session with that name already exists for this team.';
  if (msg.includes('duplicate key'))
    return 'This record already exists. Please use a different name.';
  return msg;
}

async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    },
    body: options.body ? JSON.stringify(options.body) : undefined
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(translateError(err.error || `Request failed: ${res.status}`, res.status));
  }

  if (res.status === 204) return null;
  return res.json();
}

async function downloadFile(path, filename) {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const api = {
  auth: {
    loginUrl: () => `${API_URL}/auth/login`,
    me: () => request('/auth/me'),
    refresh: () => request('/auth/refresh', { method: 'POST' })
  },

  jira: {
    projects: () => request('/jira/projects'),
    tickets: (projectKey, query) =>
      request(`/jira/tickets?projectKey=${projectKey}${query ? `&query=${encodeURIComponent(query)}` : ''}`),
    ticket: (key) => request(`/jira/tickets/${key}`),
    updatePoints: (key, body) => request(`/jira/tickets/${key}/points`, { method: 'PATCH', body }),
    writeUsage: () => request('/jira/write-usage'),
    addComment: (key, body) =>
      request(`/jira/tickets/${key}/comment`, {
        method: 'POST',
        body: body
      })
  },

  teams: {
    list: () => request('/teams'),
    create: (body) => request('/teams', { method: 'POST', body }),
    update: (id, body) => request(`/teams/${id}`, { method: 'PATCH', body }),
    delete: (id) => request(`/teams/${id}`, { method: 'DELETE' }),
    baselines: (id, points) => request(`/teams/${id}/baselines${points != null ? `?points=${points}` : ''}`),
    addBaseline: (id, body) => request(`/teams/${id}/baselines`, { method: 'POST', body }),
    deleteBaseline: (teamId, baselineId) => request(`/teams/${teamId}/baselines/${baselineId}`, { method: 'DELETE' }),
    members: (id) => request(`/teams/${id}/members`)
  },

  sessions: {
    create: (body) => request('/sessions', { method: 'POST', body }),
    get: (id) => request(`/sessions/${id}`),
    end: (id) => request(`/sessions/${id}/end`, { method: 'PATCH' }),
    delete: (id) => request(`/sessions/${id}`, { method: 'DELETE' }),
    join: (id, body) => request(`/sessions/${id}/join`, { method: 'POST', body }),
    kick: (sessionId, participantId) =>
      request(`/sessions/${sessionId}/participants/${participantId}/kick`, { method: 'PATCH' }),
    setObserver: (sessionId, participantId, isObserver) =>
      request(`/sessions/${sessionId}/participants/${participantId}/role`, { method: 'PATCH', body: { is_observer: isObserver } }),
    createRound: (id, body) => request(`/sessions/${id}/rounds`, { method: 'POST', body }),
    skipRound: (sessionId, roundId) =>
      request(`/sessions/${sessionId}/rounds/${roundId}/skip`, { method: 'PATCH' }),
    vote: (sessionId, roundId, body) =>
      request(`/sessions/${sessionId}/rounds/${roundId}/vote`, { method: 'POST', body }),
    reveal: (sessionId, roundId) =>
      request(`/sessions/${sessionId}/rounds/${roundId}/reveal`, { method: 'PATCH' }),
    revote: (sessionId, roundId) =>
      request(`/sessions/${sessionId}/rounds/${roundId}/revote`, { method: 'PATCH' }),
    complete: (sessionId, roundId, body) =>
      request(`/sessions/${sessionId}/rounds/${roundId}/complete`, { method: 'PATCH', body }),
    votes: (sessionId, roundId) =>
      request(`/sessions/${sessionId}/rounds/${roundId}/votes`),
    allVotes: (sessionId, roundId) =>
      request(`/sessions/${sessionId}/rounds/${roundId}/all-votes`),
    transferFacilitator: (sessionId, newJiraId) =>
      request(`/sessions/${sessionId}/transfer-facilitator`, {
        method: 'PATCH',
        body: { new_facilitator_jira_id: newJiraId }
      }),
    markReviewed: (sessionId, roundId) =>
      request(`/sessions/${sessionId}/rounds/${roundId}/mark-reviewed`, { method: 'PATCH' })
  },

  reports: {
    sessions: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/reports/sessions${q ? `?${q}` : ''}`);
    },
    sessionDetail: (id) => request(`/reports/sessions/${id}`),
    attendance: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/reports/attendance${q ? `?${q}` : ''}`);
    },
    uncertain: (teamId) => request(`/reports/uncertain${teamId ? `?team_id=${teamId}` : ''}`),
    baselines: (teamId) => request(`/reports/baselines${teamId ? `?team_id=${teamId}` : ''}`),
    exportSessionCsv: (id) => `${API_URL}/reports/sessions/${id}/export/csv`,
    exportSessionPdf: (id) => `${API_URL}/reports/sessions/${id}/export/pdf`,
    exportAttendanceCsv: (teamName) =>
      `${API_URL}/reports/attendance/export/csv${teamName ? `?team_name=${encodeURIComponent(teamName)}` : ''}`,
    exportAttendancePdf: (teamName) =>
      `${API_URL}/reports/attendance/export/pdf${teamName ? `?team_name=${encodeURIComponent(teamName)}` : ''}`,
    downloadSessionCsv: (id) => downloadFile(`/reports/sessions/${id}/export/csv`, `session-${id}.csv`),
    downloadSessionPdf: (id) => downloadFile(`/reports/sessions/${id}/export/pdf`, `session-${id}.pdf`),
    downloadAttendanceCsv: (teamName) => downloadFile(`/reports/attendance/export/csv${teamName ? `?team_name=${encodeURIComponent(teamName)}` : ''}`, 'attendance.csv'),
    downloadAttendancePdf: (teamName) => downloadFile(`/reports/attendance/export/pdf${teamName ? `?team_name=${encodeURIComponent(teamName)}` : ''}`, 'attendance.pdf')
  }
};
